import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

const styles = () => ({
	intro: {
		color: '#666',
		fontSize: 14,
		marginBottom: 8,
	},
	switchBlock: {
		marginTop: 16,
	},
	switchHeader: {
		fontWeight: 600,
		fontSize: 15,
		marginBottom: 8,
	},
	sectionHeader: {
		textTransform: 'uppercase',
		fontSize: 12,
		color: '#777',
		fontWeight: 900,
		letterSpacing: 1,
		marginTop: 16,
		marginBottom: 4,
	},
	inventoryLine: {
		fontSize: 14,
		marginTop: 8,
	},
	actionChoice: {
		marginTop: 8,
	},
	actionOptionNote: {
		color: '#666',
		fontSize: 13,
		margin: '0px 0px 8px 30px',
	},
	warnings: {
		background: '#FFF8E1',
		border: '1px solid #FFE082',
		borderRadius: 4,
		padding: '8px 12px',
		margin: '12px 0px 0px 0px',
		fontSize: 13,
	},
	keepNote: {
		color: '#666',
		fontSize: 13,
		marginTop: 16,
	},
});

export const tankSwitchKey = (tankSwitch) =>
	`${tankSwitch.oldTankNumber}_${tankSwitch.newTankNumber}`;

const switchHasInventoryWork = (tankSwitch) =>
	!!(
		tankSwitch.veederInventoryCount ||
		tankSwitch.hiddenInventoryCount ||
		tankSwitch.atgDeliveryRecordCount ||
		tankSwitch.tankDailySalesCount
	);

export const pluralize = (count, singular, plural) => (count === 1 ? singular : plural);

//- What a purge/migrate acts on: visible entries always; hidden ones only on a migrate
//- (purge leaves them alone), including when the visible count is zero.
const describeReadingCounts = ({ visible, hidden, isMigrate }) => {
	if (!visible) {
		return `${hidden.toLocaleString()} hidden tank inventory ${pluralize(
			hidden,
			'entry',
			'entries'
		)}`;
	}
	const hiddenSuffix = isMigrate && hidden > 0 ? ` (plus ${hidden.toLocaleString()} hidden)` : '';
	return `${visible.toLocaleString()} tank inventory ${pluralize(
		visible,
		'entry',
		'entries'
	)}${hiddenSuffix}`;
};

/**
 * Confirm dialog shown after a station save changed a tank number and old data was found
 * still pointing at the old number. Lists the upcoming scheduled deliveries that will be
 * repointed and the old tank's inventory history, with a per-switch choice to purge that
 * history (remove it from monitoring) or migrate it onto the new tank number; confirming
 * runs station.applyTankSwitchCleanup per switch, skipping leaves the old data alone.
 */
const TankSwitchCleanupDialog = ({
	classes,
	tankSwitches,
	completedCount = 0,
	isWorking,
	onConfirm,
	onSkip,
}) => {
	const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);
	//- key -> 'purge' | 'migrate'; anything unset means purge (the original behavior)
	const [inventoryActions, setInventoryActions] = useState({});

	if (_.isEmpty(tankSwitches)) {
		return null;
	}

	//- The backend refuses the cleanup unless warnings are explicitly acknowledged.
	const hasWarnings = tankSwitches.some((tankSwitch) => !_.isEmpty(tankSwitch.warnings));

	const actionFor = (tankSwitch) => inventoryActions[tankSwitchKey(tankSwitch)] || 'purge';

	return (
		<Dialog open maxWidth="md" fullWidth>
			<DialogTitle>
				{tankSwitches.length === 1
					? `Tank ${tankSwitches[0].oldTankNumber} was changed to Tank ${tankSwitches[0].newTankNumber}. Clean up old data?`
					: 'Tank numbers were changed. Clean up old data?'}
			</DialogTitle>
			<DialogContent>
				<div className={classes.intro}>
					The station was saved. Some older data still points at the old tank number
					{tankSwitches.length > 1 ? 's' : ''} and can be updated now.
				</div>

				{tankSwitches.map((tankSwitch, index) => {
					const {
						oldTankNumber,
						newTankNumber,
						scheduledDeliveries,
						veederInventoryCount,
						hiddenInventoryCount,
						atgDeliveryRecordCount,
						tankDailySalesCount,
						newTankInventoryCount,
					} = tankSwitch;
					const action = actionFor(tankSwitch);
					const isMigrate = action === 'migrate';
					//- Applied before an error stopped a later switch: a retry resumes past it,
					//- so lock its choice and say so.
					const isCompleted = index < completedCount;
					//- The backend refuses a migrate for these reasons (two different tanks'
					//- histories must not merge), so don't offer it.
					const migrateBlockedReasons = tankSwitch.migrateBlockedReasons || [];
					const migrateBlocked = migrateBlockedReasons.length > 0;
					//- purge only hides visible readings and deletes daily sales rows; with
					//- neither present it would change nothing
					const purgeIsNoOp = !veederInventoryCount && !tankDailySalesCount;

					return (
						<div key={tankSwitchKey(tankSwitch)} className={classes.switchBlock}>
							{tankSwitches.length > 1 && (
								<div className={classes.switchHeader}>
									Tank {oldTankNumber} → Tank {newTankNumber}
								</div>
							)}

							{isCompleted && (
								<div className={classes.inventoryLine}>
									✓ Already cleaned up before the last error — shown for reference only; a retry
									will not run it again.
								</div>
							)}

							{!_.isEmpty(scheduledDeliveries) && (
								<>
									<div className={classes.sectionHeader}>
										Scheduled deliveries to move to Tank {newTankNumber} (
										{scheduledDeliveries.length})
									</div>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Delivery Id</TableCell>
												<TableCell>Status</TableCell>
												<TableCell>Delivery Window Start</TableCell>
												<TableCell>Tank</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{scheduledDeliveries.map((d) => (
												<TableRow key={`${d.stationDeliveryId}_${d.leg}`}>
													<TableCell>{d.stationDeliveryId}</TableCell>
													<TableCell>{d.status}</TableCell>
													<TableCell>{d.deliveryWindowStart || '-'}</TableCell>
													<TableCell>
														{oldTankNumber} → {newTankNumber}
														{d.leg === 'secondary' ? ' (split leg)' : ''}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</>
							)}

							{switchHasInventoryWork(tankSwitch) && (
								<div className={classes.actionChoice}>
									<div className={classes.sectionHeader}>
										Tank {oldTankNumber} inventory history
									</div>

									{(veederInventoryCount > 0 || (isMigrate && hiddenInventoryCount > 0)) && (
										<div className={classes.inventoryLine}>
											{describeReadingCounts({
												visible: veederInventoryCount,
												hidden: hiddenInventoryCount,
												isMigrate,
											})}{' '}
											{isMigrate
												? `will be moved to Tank ${newTankNumber}.`
												: 'will be removed from monitoring.'}
											{isMigrate && newTankInventoryCount > 0 && (
												<>
													{' '}
													Tank {newTankNumber} already has {newTankInventoryCount.toLocaleString()}{' '}
													{pluralize(newTankInventoryCount, 'entry', 'entries')}; they will be
													combined.
												</>
											)}
										</div>
									)}

									{tankDailySalesCount > 0 && (
										<div className={classes.inventoryLine}>
											{tankDailySalesCount.toLocaleString()}{' '}
											{pluralize(tankDailySalesCount, 'day', 'days')} of daily sales history{' '}
											{isMigrate
												? `will be moved to Tank ${newTankNumber}. On days Tank ${newTankNumber} already has sales history, its own row is kept.`
												: 'will be deleted.'}
										</div>
									)}

									{atgDeliveryRecordCount > 0 && (
										<div className={classes.inventoryLine}>
											{atgDeliveryRecordCount.toLocaleString()} ATG delivery{' '}
											{pluralize(atgDeliveryRecordCount, 'record', 'records')}{' '}
											{isMigrate
												? `will be moved to Tank ${newTankNumber}.`
												: 'will be left as they are.'}
										</div>
									)}

									<RadioGroup
										value={action}
										onChange={(event) =>
											setInventoryActions({
												...inventoryActions,
												[tankSwitchKey(tankSwitch)]: event.target.value,
											})
										}
									>
										<FormControlLabel
											value="purge"
											control={<Radio />}
											label="Purge"
											disabled={isCompleted}
										/>
										<div className={classes.actionOptionNote}>
											The old tank number was wrong data — remove its inventory entries from
											monitoring and delete its daily sales history.
											{purgeIsNoOp &&
												' Nothing visible remains here for purge to remove — purging would change nothing for this tank.'}
										</div>
										<FormControlLabel
											value="migrate"
											control={<Radio />}
											label="Migrate"
											disabled={isCompleted || migrateBlocked}
										/>
										{migrateBlocked ? (
											<div className={classes.actionOptionNote}>
												Unavailable: {migrateBlockedReasons.join('; ')} — they are two different
												tanks, and their history cannot be combined.
											</div>
										) : (
											<div className={classes.actionOptionNote}>
												It is the same physical tank with a new number — keep its history by moving
												it to the new tank number.
											</div>
										)}
									</RadioGroup>
								</div>
							)}

							{!_.isEmpty(tankSwitch.warnings) && (
								<div className={classes.warnings}>
									{tankSwitch.warnings.map((warning) => (
										<div key={warning}>⚠️ {warning}</div>
									))}
								</div>
							)}
						</div>
					);
				})}

				<div className={classes.keepNote}>
					Past deliveries are not changed — they keep the tank they were actually delivered to.
					Skipping leaves everything as it is today.
				</div>

				{hasWarnings && (
					<FormControlLabel
						control={
							<Checkbox
								checked={warningsAcknowledged}
								onChange={(event) => setWarningsAcknowledged(event.target.checked)}
							/>
						}
						label="I have read the warnings above and want to proceed anyway"
					/>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onSkip} disabled={isWorking}>
					Skip
				</Button>
				<Button
					onClick={() =>
						onConfirm({
							acknowledgeWarnings: hasWarnings && warningsAcknowledged,
							inventoryActions,
						})
					}
					color="primary"
					variant="contained"
					disabled={isWorking || (hasWarnings && !warningsAcknowledged)}
				>
					{isWorking ? 'Updating...' : 'Update Old Data'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

TankSwitchCleanupDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	tankSwitches: PropTypes.array,
	completedCount: PropTypes.number,
	isWorking: PropTypes.bool,
	onConfirm: PropTypes.func.isRequired,
	onSkip: PropTypes.func.isRequired,
};

export default withStyles(styles)(TankSwitchCleanupDialog);
